import type { ForecastDay } from '../../lib/weather';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';
import { WeatherChip } from './WeatherChip';

// "Today's conditions" callout — small card near the top of the
// Capacity tile that fronts the day's weather as a planning input.
// Pairs a large temp + icon (WeatherChip size="lg") with a one-line
// interpretation that ties weather back to capacity reasoning.
//
// The interpretation is computed from BOTH the weather AND the
// barber's historical walk-in fraction for today's day-of-week
// (passed in by CapacityTile). The same data the 7-day forecast uses
// to widen its bands feeds this copy:
//   - storm + walk-in-heavy → "Storm forecast — expect a slow day."
//   - storm + appointment-heavy → "Storm forecast — booked appts should hold."
//   - clear + walk-in-heavy → "Clear skies — prime walk-in conditions."
//   - clear + appointment-heavy → "Clear day — usual booked schedule."
//
// Why a separate callout instead of just leaving weather on the
// heatmap chips: the barber's first capacity question every morning
// is "is today going to be busy?" The callout answers that directly
// instead of making them scan a 7×16 grid to infer it.

const APPOINTMENT_HEAVY_THRESHOLD = 0.4;

type CapacityHintKind =
  | 'storm'
  | 'severeRain'
  | 'severeSnow'
  | 'rain'
  | 'snow'
  | 'heat'
  | 'cold'
  | 'fog'
  | 'clear';

export function TodayConditionsCallout({
  today,
  walkinFractionToday,
}: {
  /** Today's weather, looked up by date from the weatherByDate map.
   *  Null → callout renders nothing (no zip / API down). */
  today: ForecastDay | null;
  /** Historical walk-in fraction for TODAY's day-of-week (0–1).
   *  Drives the appointment-soft vs walk-in-heavy interpretation.
   *  Defaults to 1.0 (treat as walk-in-heavy) when the barber
   *  hasn't tagged enough cuts — same conservative default the
   *  forecast model uses. */
  walkinFractionToday: number;
}) {
  const t = useT();
  const language = useUI((s) => s.language);
  const dateLocale = language === 'es' ? 'es-US' : 'en-US';

  if (!today) return null;

  const kind = capacityHintKind(today);
  // Appointment-heavy barbers get a softened message — their booked
  // clients still show up to rainy haircuts. Only flips for
  // weather-impacted conditions (clear / fog stay the same regardless
  // of mix).
  const softenedByAppointments =
    walkinFractionToday < APPOINTMENT_HEAVY_THRESHOLD &&
    kind !== 'clear' &&
    kind !== 'fog';
  const variant = softenedByAppointments ? 'soft' : 'hit';
  const bodyKey = `dash.capacity.today.hint.${kind}.${variant}`;

  // "Today" label includes the date so the callout reads as a
  // dated snapshot, not an evergreen blurb.
  const dateLabel = new Date().toLocaleDateString(dateLocale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="rounded-xl border border-line bg-bg-alt p-3 flex items-center gap-3">
      {/* Big weather chip — fronts the temp + condition. The chip's
          color encoding (amber sunny / red storm / blue rain etc.)
          carries the visual urgency without needing extra styling on
          the callout container itself. */}
      <div className="shrink-0">
        <WeatherChip day={today} size="lg" />
      </div>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
          {t('dash.capacity.today.eyebrow', { date: dateLabel })}
        </div>
        <p className="text-[13px] font-bold text-ink-900 dark:text-ink-100 mt-0.5">
          {t(bodyKey)}
        </p>
        {/* Low temperature subline — small + muted so it doesn't
            compete with the headline interpretation. Useful for
            cold-snap mornings ("high is 60° but low is 20°"). */}
        <p className="text-[11px] text-ink-500 mt-0.5 tabular-nums">
          {t('dash.capacity.today.lowLine', { low: today.lowF })}
        </p>
      </div>
    </div>
  );
}

// Maps a forecast day → which capacity-planning bucket it belongs to.
// More specific than weather.condition alone because we want to call
// out heat / cold thresholds and severe variants ("storm" is its own
// thing vs. "heavy rain w/ alert"). Severity-ordered: first match
// wins.
function capacityHintKind(day: ForecastDay): CapacityHintKind {
  if (day.condition === 'storm') return 'storm';
  if (day.condition === 'rainy' && day.alert) return 'severeRain';
  if (day.condition === 'snow' && day.alert) return 'severeSnow';
  if (day.condition === 'rainy') return 'rain';
  if (day.condition === 'snow') return 'snow';
  if (day.highF >= 95) return 'heat';
  if (day.lowF <= 25) return 'cold';
  if (day.condition === 'fog') return 'fog';
  return 'clear';
}
